import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import jobWorksApi from '../../api/jobWorks';

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  jobWorkId: number;
  jobWorkCode: string;
  existingInvoice?: {
    invoiceNumber?: string;
    invoiceDate?: string;
    invoiceAmount?: number;
    taxAmount?: number;
    invoiceImageUrl?: string;
  };
  onSave: () => void;
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  open,
  onClose,
  jobWorkId,
  jobWorkCode,
  existingInvoice,
  onSave
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [invoiceAmount, setInvoiceAmount] = useState<number>(0);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [invoiceImageUrl, setInvoiceImageUrl] = useState('');
  const [invoiceFileType, setInvoiceFileType] = useState<'image' | 'pdf' | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate from existing invoice if editing
  useEffect(() => {
    if (open) {
      if (existingInvoice) {
        setInvoiceNumber(existingInvoice.invoiceNumber || '');
        setInvoiceDate(existingInvoice.invoiceDate ? new Date(existingInvoice.invoiceDate) : null);
        setInvoiceAmount(existingInvoice.invoiceAmount || 0);
        setTaxAmount(existingInvoice.taxAmount || 0);
        setInvoiceImageUrl(existingInvoice.invoiceImageUrl || '');
        if (existingInvoice.invoiceImageUrl) {
          setInvoiceFileType(existingInvoice.invoiceImageUrl.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image');
        }
      } else {
        setInvoiceNumber('');
        setInvoiceDate(null);
        setInvoiceAmount(0);
        setTaxAmount(0);
        setInvoiceImageUrl('');
        setInvoiceFileType(null);
      }
      setErrors({});
    }
  }, [open, existingInvoice]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }
    if (!invoiceAmount && invoiceAmount !== 0) {
      newErrors.invoiceAmount = 'Invoice amount is required';
    }
    if (!invoiceImageUrl) {
      newErrors.invoiceImageUrl = 'Invoice attachment is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      enqueueSnackbar('Please fill all required fields', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);

      await jobWorksApi.addInvoice(jobWorkId, {
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate: invoiceDate ? format(invoiceDate, 'yyyy-MM-dd') : undefined,
        invoiceAmount,
        taxAmount: taxAmount || 0,
        invoiceImageUrl
      });

      enqueueSnackbar('Invoice saved successfully', { variant: 'success' });
      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      enqueueSnackbar(error?.response?.data?.error || 'Failed to save invoice', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingInvoice(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await jobWorksApi.uploadInvoiceImage(formData);
      setInvoiceImageUrl(response.url);
      setInvoiceFileType(file.type.includes('pdf') ? 'pdf' : 'image');
      setErrors(prev => ({ ...prev, invoiceImageUrl: '' }));
      enqueueSnackbar('Invoice uploaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error uploading invoice:', error);
      enqueueSnackbar('Failed to upload invoice', { variant: 'error' });
    } finally {
      setUploadingInvoice(false);
    }
  };

  const handleRemoveInvoice = () => {
    setInvoiceImageUrl('');
    setInvoiceFileType(null);
  };

  const totalAmount = (invoiceAmount || 0) + (taxAmount || 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {existingInvoice?.invoiceNumber ? 'Update Invoice' : 'Add Invoice'} — {jobWorkCode}
      </DialogTitle>

      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Invoice Number"
                value={invoiceNumber}
                onChange={(e) => {
                  setInvoiceNumber(e.target.value);
                  if (errors.invoiceNumber) setErrors(prev => ({ ...prev, invoiceNumber: '' }));
                }}
                fullWidth
                required
                error={!!errors.invoiceNumber}
                helperText={errors.invoiceNumber}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Invoice Date"
                value={invoiceDate}
                onChange={(date) => setInvoiceDate(date)}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Invoice Amount"
                value={invoiceAmount}
                onChange={(e) => {
                  setInvoiceAmount(Number(e.target.value) || 0);
                  if (errors.invoiceAmount) setErrors(prev => ({ ...prev, invoiceAmount: '' }));
                }}
                fullWidth
                required
                type="number"
                error={!!errors.invoiceAmount}
                helperText={errors.invoiceAmount}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Tax Amount (GST etc.)"
                value={taxAmount}
                onChange={(e) => setTaxAmount(Number(e.target.value) || 0)}
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Total: ₹{totalAmount.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Invoice Copy <Typography component="span" color="error">*</Typography>
              </Typography>
              {errors.invoiceImageUrl && (
                <Alert severity="error" sx={{ mb: 1 }}>{errors.invoiceImageUrl}</Alert>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={uploadingInvoice}
                >
                  {uploadingInvoice ? 'Uploading...' : (invoiceImageUrl ? 'Replace' : 'Upload Invoice')}
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf"
                    onChange={handleInvoiceUpload}
                  />
                </Button>
                {invoiceImageUrl && (
                  <IconButton color="error" onClick={handleRemoveInvoice} size="small" title="Remove invoice">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              {/* Invoice preview */}
              {invoiceImageUrl && (
                <Box sx={{ mt: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {invoiceFileType === 'pdf' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PictureAsPdfIcon color="error" />
                      <Typography variant="body2">PDF Invoice attached</Typography>
                      <Button size="small" href={invoiceImageUrl} target="_blank">View</Button>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <InsertPhotoIcon color="primary" />
                        <Typography variant="body2">Invoice image attached</Typography>
                      </Box>
                      <Box
                        component="img"
                        src={invoiceImageUrl}
                        alt="Invoice"
                        sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDialog;
